<template>
  <div class="step-preview">
    <div ref="frame" class="preview-frame">
      <div ref="inner" class="preview-inner" :style="innerStyle">
        <component :is="preview.component" v-bind="preview.props" />
      </div>
    </div>
    <p class="preview-caption">{{ preview.caption }}</p>
    <p v-if="preview.disclaimer" class="preview-disclaimer">
      {{ preview.disclaimer }}
    </p>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";

// One live preview (rewards card, sign-up page or counter sign) with its
// caption and optional disclaimer. The registration stepper renders it in
// the sticky desktop aside and the "Your branding" step renders it inline
// on narrow screens, so the framing lives in one place.
//
// The mockups have fixed widths (280px to 312px). When the frame is
// narrower than that, the mockup is scaled down to fit rather than trimmed,
// and its layout box is pulled in to the scaled size so the frame collapses
// with it. Nothing is clipped, so the mockups' shadows stay intact.
export default {
  name: "StepPreview",
  props: {
    // { component, props, caption, disclaimer }
    preview: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const frame = ref(null);
    const inner = ref(null);
    const scale = ref(1);
    const innerWidth = ref(0);
    const innerHeight = ref(0);

    const measure = () => {
      const frameEl = frame.value;
      const innerEl = inner.value;
      if (!frameEl || !innerEl) return;
      const naturalWidth = innerEl.offsetWidth;
      const naturalHeight = innerEl.offsetHeight;
      const available = frameEl.clientWidth;
      const nextScale =
        naturalWidth > 0 && available > 0
          ? Math.min(1, available / naturalWidth)
          : 1;
      // Only write when something changed, so the resize that follows
      // (the frame taking its new height) can't feed back into a loop.
      if (nextScale !== scale.value) scale.value = nextScale;
      if (naturalWidth !== innerWidth.value) innerWidth.value = naturalWidth;
      if (naturalHeight !== innerHeight.value) innerHeight.value = naturalHeight;
    };

    // Measuring synchronously inside a ResizeObserver callback and then
    // resizing an ancestor (the frame) trips the browser's "ResizeObserver
    // loop" error, so the measurement is deferred to the next frame.
    let rafId = null;
    const scheduleMeasure = () => {
      if (typeof window === "undefined") return;
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        measure();
      });
    };

    let observer = null;

    onMounted(() => {
      scheduleMeasure();
      // Observe the mockup only (it changes when a panel opens or the
      // preview swaps); never the frame, whose height this component sets.
      if (typeof ResizeObserver !== "undefined" && inner.value) {
        observer = new ResizeObserver(scheduleMeasure);
        observer.observe(inner.value);
      }
      if (typeof window !== "undefined") {
        window.addEventListener("resize", scheduleMeasure);
      }
    });

    onBeforeUnmount(() => {
      if (observer) observer.disconnect();
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", scheduleMeasure);
        if (rafId !== null) window.cancelAnimationFrame(rafId);
      }
    });

    // A transform leaves the mockup's layout box at its natural size, so the
    // leftover is pulled back in with negative margins. The frame then sizes
    // itself to the scaled mockup without an overflow clip, which would trim
    // the mockup's shadow.
    const innerStyle = computed(() => {
      if (scale.value >= 1) return {};
      const trimX = (innerWidth.value * (1 - scale.value)) / 2;
      const trimY = innerHeight.value * (1 - scale.value);
      return {
        transform: `scale(${scale.value})`,
        transformOrigin: "top center",
        margin: `0 ${-trimX}px ${-trimY}px`,
      };
    });

    return {
      frame,
      inner,
      innerStyle,
    };
  },
};
</script>

<style scoped>
.step-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.preview-frame {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

/* Natural size of the mockup, measured before any transform. Its scaled
   size is applied through inline margins (see innerStyle). */
.preview-inner {
  display: inline-flex;
  flex-shrink: 0;
}

.preview-caption {
  margin: 12px 0 0;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4a5568;
  text-align: center;
}

.preview-disclaimer {
  margin: 8px 0 0;
  max-width: 280px;
  font-size: 12px;
  line-height: 1.45;
  color: #4a5568;
  text-align: center;
  font-style: italic;
}
</style>
